import type { ElectronApi } from '../../shared/electron-api';
import type { ClipboardFileData, ClipboardFileInfo, ClipboardFileKind } from '../../shared/clipboard-file';
import type {
  ClipboardAckMessage,
  ClipboardReadMessage,
  ClipboardWriteMessage,
  VideoSettingsAckMessage,
  VideoSettingsMessage,
  InputMessage,
  HostMessage,
  RemoteCursorMessage,
  RemoteCursorModeMessage,
} from '../../shared/datachannel';
import type {
  FileClipboardAck,
  FileControl,
  FileAck,
  FileMeta,
  FileResumeResponse,
  FileTransferProgress,
  IncrementalSha256,
} from '../../shared/filexfer';
import type { PlatformKind } from '../../shared/platform';
import type { WebRtcConnectionPath, WebRtcConnectionMetrics, WebRtcTrafficSample } from '../../shared/rtc-path';
import type { RemoteStreamSettings } from '../../shared/stream-profile';
import type { ConnectionDiagnostics, ConnectionState } from '../../shared/connection-state';

// Keep enough file data queued to cover normal WAN RTT without letting file
// transfers grow without bound. The low-water event refills this in ~512 KB bursts.
// A wider bounded send window prevents high-RTT P2P links from draining between
// small bursts; it still caps queued file data to roughly 4MB per transfer.
export const FILE_BUFFER_HIGH = 4 * 1024 * 1024;
export const FILE_BUFFER_LOW = 2 * 1024 * 1024;
export const FILE_STREAM_WRITE_BATCH = 1024 * 1024;
export const FILE_RECEIVE_IDLE_TIMEOUT_MS = 60_000;
export const POINTER_BUFFER_MAX = 128 * 1024;
export const WHEEL_BUFFER_MAX = 64 * 1024;
export const CLIPBOARD_ACK_TIMEOUT_MS = 1_800;
export const CLIPBOARD_MAX_ATTEMPTS = 2;
export const FILE_CLIPBOARD_ACK_TIMEOUT_MS = 60_000;

export interface RemotePeerInfo {
  deviceId: string;
  platform: string;
  os: PlatformKind;
  hostname: string;
  deviceName: string;
}

export interface PendingClipboardTransfer {
  id: string;
  text: string;
  html?: string;
  rtf?: string;
  channel: RTCDataChannel;
  attempts: number;
  timer: ReturnType<typeof setTimeout> | null;
  resolve: (ok: boolean) => void;
}

export interface PendingFileClipboardTransfer {
  id: string;
  resolve: (ok: boolean) => void;
  timer: ReturnType<typeof setTimeout> | null;
}

export interface PendingFileResume {
  id: string;
  resolve: (response: FileResumeResponse | null) => void;
  timer: ReturnType<typeof setTimeout> | null;
}

export interface PendingFileAck {
  id: string;
  resolve: (ok: boolean | null) => void;
  timer: ReturnType<typeof setTimeout> | null;
}

export interface ReceivingFile {
  id: string;
  name: string;
  size: number;
  purpose: 'download' | 'clipboard';
  kind?: ClipboardFileKind;
  mimeType?: string;
  clipboardId?: string;
  clipboardIndex?: number;
  clipboardTotal?: number;
  chunks: Uint8Array[];
  streamId?: string;
  streamResumed: boolean;
  streamBuffer: Uint8Array[];
  streamBufferedBytes: number;
  received: number;
  acknowledgedOffset: number;
  hash: IncrementalSha256;
}

export interface ReceivingClipboardBatch {
  id: string;
  total: number;
  files: Array<ClipboardFileData | null>;
}

export interface ControllerCallbacks {
  onStatus(text: string, state?: 'idle' | 'online' | 'error'): void;
  onStream(stream: MediaStream): void;
  onDevice(text: string): void;
  onConnectionState?(state: ConnectionState): void;
  onPeerInfo?(info: RemotePeerInfo | null): void;
  onConnectionPath?(info: WebRtcConnectionPath): void;
  onConnectionQuality?(metrics: WebRtcConnectionMetrics): void;
  onFirstFrame?(elapsedMs: number | null): void;
  onInputRtt?(elapsedMs: number | null): void;
  onConnectionStalled?(stalled: boolean): void;
  onReconnectExhausted?(): void;
  onControlReady?(ready: boolean): void;
  onRemoteInputReady?(ready: boolean, detail?: string): void;
  onClipboard?(text: string, copied: boolean): void;
  onClipboardFiles?(files: ClipboardFileInfo[], copied: boolean): void;
  onFileTransfer?(progress: FileTransferProgress): void;
  onRemoteCursor?(cursor: RemoteCursorMessage): void;
  onRemoteCursorMode?(mode: RemoteCursorModeMessage['mode']): void;
  onVideoSettings?(settings: VideoSettingsAckMessage): void;
}

// 重新导出供各管理器模块使用的共享类型/常量/函数。
export {
  type ElectronApi,
  type ClipboardFileData,
  type ClipboardFileInfo,
  type ClipboardAckMessage,
  type ClipboardReadMessage,
  type ClipboardWriteMessage,
  type VideoSettingsAckMessage,
  type VideoSettingsMessage,
  type InputMessage,
  type HostMessage,
  type RemoteCursorMessage,
  type RemoteCursorModeMessage,
  type FileClipboardAck,
  type FileControl,
  type FileAck,
  type FileMeta,
  type FileResumeResponse,
  type FileTransferProgress,
  type PlatformKind,
  type WebRtcConnectionPath,
  type WebRtcConnectionMetrics,
  type WebRtcTrafficSample,
  type RemoteStreamSettings,
  type ConnectionDiagnostics,
  type ConnectionState,
};
